import { useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type WidgetConfig } from "../../api";
import { useI18n, type Dict } from "../../lib/i18n";
import { WidgetMock } from "../../components/WidgetMock";
import { cardConfig, selectPreset, setRadius, showsLauncherText, usesSuggestedColours, withLookDefaults } from "../../lib/widgetLook";
import { PRESET_IDS, PRESETS } from "../../../../chat-widget/src/presets";
import { FeatureHint, PlanLimitNotice, useFeature, usePlanLimit } from "../../components/PlanNotice";
import { ReadOnlyNote, Section, SitePicker, StatusLine, Switch, useSelectedSite, useSites, useStatus } from "./common";

/**
 * How the chat bubble looks and speaks on a customer's site, with a preview
 * that changes as the form does. The preview and the gallery cards are drawn
 * with the widget's own stylesheet (see WidgetMock), so a preset looks here
 * exactly as it will on the site.
 */

const MAX_QUICK = 6;
const MAX_FAQ = 10;

/** The fine-tuning choices, defaults first — see chat-widget/src/presets.ts. */
const FONTS = ["preset", "system", "rounded", "serif", "mono"] as const;
const LAUNCHERS = ["preset", "circle", "square", "pill"] as const;
const FONT_LABEL: Record<(typeof FONTS)[number], (t: Dict["widget"]) => string> = {
  preset: (t) => t.fontPreset,
  system: (t) => t.fontSystem,
  rounded: (t) => t.fontRounded,
  serif: (t) => t.fontSerif,
  mono: (t) => t.fontMono,
};
const LAUNCHER_LABEL: Record<(typeof LAUNCHERS)[number], (t: Dict["widget"]) => string> = {
  preset: (t) => t.launcherPreset,
  circle: (t) => t.launcherCircle,
  square: (t) => t.launcherSquare,
  pill: (t) => t.launcherPill,
};

/**
 * What a plan without the `themes` feature may still pick: the default (the
 * first entry) and whatever is already saved. Enough to undo fine-tuning done
 * before a downgrade — the server allows a move back to the default — and not
 * enough to add any more of it.
 */
export function choices<T extends string>(canTheme: boolean, all: readonly T[], saved: T): T[] {
  if (canTheme) return [...all];
  return all.filter((id) => id === all[0] || id === saved);
}

export function WidgetTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.widget;
  const { sites, reload } = useSites();
  const [site, selectSite] = useSelectedSite(sites);
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [newQuick, setNewQuick] = useState("");
  const [newFaq, setNewFaq] = useState({ q: "", a: "" });
  const [status, show, fail] = useStatus();
  const { limit, catchLimit, clearLimit } = usePlanLimit();
  // Below Pro only the classic preset and its defaults may be saved, so the
  // gallery and the fine-tuning are shown but locked rather than hidden: a
  // look chosen before a downgrade is still stored, and still visible.
  const canTheme = useFeature("themes");
  // Off by default: a preset is a shell, and a site's brand colour is not
  // something to replace by accident while browsing the gallery.
  const [applyColours, setApplyColours] = useState(false);

  useEffect(() => {
    setConfig(site ? withLookDefaults(site.widgetConfig) : null);
  }, [site?.id, site?.widgetConfig]);

  if (!site || !config) return <div class="empty">{all.common.createSiteFirst}</div>;

  const saved = withLookDefaults(site.widgetConfig);
  const set = <K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]): void => setConfig({ ...config, [key]: value });
  const dirty = JSON.stringify(config) !== JSON.stringify(saved);

  function addQuick(): void {
    const text = newQuick.trim();
    if (!text || config!.quickReplies.includes(text) || config!.quickReplies.length >= MAX_QUICK) return;
    set("quickReplies", [...config!.quickReplies, text]);
    setNewQuick("");
  }

  function moveQuick(index: number, step: -1 | 1): void {
    const list = config!.quickReplies.slice();
    const target = index + step;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target]!, list[index]!];
    set("quickReplies", list);
  }

  function addFaq(): void {
    const q = newFaq.q.trim();
    const a = newFaq.a.trim();
    if (!q || !a || config!.faq.length >= MAX_FAQ || config!.faq.some((x) => x.q === q)) return;
    set("faq", [...config!.faq, { q, a }]);
    setNewFaq({ q: "", a: "" });
  }

  function moveFaq(index: number, step: -1 | 1): void {
    const list = config!.faq.slice();
    const target = index + step;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target]!, list[index]!];
    set("faq", list);
  }

  async function save(): Promise<void> {
    try {
      clearLimit();
      await api.updateSite(site!.id, { widgetConfig: config });
      await reload();
      show("ok", t.saved);
    } catch (err) {
      if (!catchLimit(err)) fail(err);
    }
  }

  const disabled = !isAdmin;
  const clamp = (v: string): number => Math.max(0, Math.min(200, Number(v) || 0));
  const fonts = choices(canTheme, FONTS, config.font);
  const launchers = choices(canTheme, LAUNCHERS, config.launcherStyle);

  return (
    <>
      <ReadOnlyNote isAdmin={isAdmin} />
      <PlanLimitNotice limit={limit} />
      <div class="widget-layout">
        <div>
          <SitePicker sites={sites} value={site} onChange={selectSite} />

          <Section title={t.welcome}>
            <label>
              {t.language}
              <select value={config.locale} disabled={disabled} onChange={(e) => set("locale", e.currentTarget.value as WidgetConfig["locale"])}>
                <option value="en">{t.langEn}</option>
                <option value="tr">{t.langTr}</option>
              </select>
            </label>
            <label>
              {t.title}
              <input type="text" maxLength={60} value={config.title} disabled={disabled} onInput={(e) => set("title", e.currentTarget.value)} />
            </label>
            <label>
              {t.subtitle}
              <input type="text" maxLength={120} value={config.subtitle} disabled={disabled} onInput={(e) => set("subtitle", e.currentTarget.value)} />
            </label>
            <label>
              {t.greeting} <span class="hint">{t.greetingHint}</span>
              <textarea class="plain" maxLength={500} value={config.greeting} disabled={disabled} onInput={(e) => set("greeting", e.currentTarget.value)} />
            </label>
            <label>
              {t.placeholder}
              <input type="text" maxLength={120} value={config.placeholder} disabled={disabled} onInput={(e) => set("placeholder", e.currentTarget.value)} />
            </label>
          </Section>

          <Section title={t.quickTitle} intro={t.quickIntro(MAX_QUICK)}>
            {config.quickReplies.length > 0 ? (
              <ul class="quick-list">
                {config.quickReplies.map((q, i) => (
                  <li key={q}>
                    <span>{q}</span>
                    {isAdmin ? (
                      <span class="quick-actions">
                        <button class="btn ghost icon" aria-label={t.up(q)} disabled={i === 0} onClick={() => moveQuick(i, -1)}>
                          ↑
                        </button>
                        <button
                          class="btn ghost icon"
                          aria-label={t.down(q)}
                          disabled={i === config.quickReplies.length - 1}
                          onClick={() => moveQuick(i, 1)}
                        >
                          ↓
                        </button>
                        <button class="btn ghost icon" aria-label={t.remove(q)} onClick={() => set("quickReplies", config.quickReplies.filter((x) => x !== q))}>
                          ×
                        </button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p class="hint">{t.quickNone}</p>
            )}
            {isAdmin && config.quickReplies.length < MAX_QUICK ? (
              <form
                class="form-inline"
                onSubmit={(e) => {
                  e.preventDefault();
                  addQuick();
                }}
              >
                <label>
                  {t.quickNew}
                  <input type="text" maxLength={40} placeholder={t.quickExample} value={newQuick} onInput={(e) => setNewQuick(e.currentTarget.value)} />
                </label>
                <button class="btn" disabled={!newQuick.trim()}>
                  {all.common.add}
                </button>
              </form>
            ) : null}
          </Section>

          <Section title={t.faqTitle} intro={t.faqIntro(MAX_FAQ)}>
            {config.faq.length > 0 ? (
              <ul class="faq-list">
                {config.faq.map((item, i) => (
                  <li key={item.q}>
                    <div class="faq-text">
                      <strong>{item.q}</strong>
                      <span>{item.a}</span>
                    </div>
                    {isAdmin ? (
                      <span class="quick-actions">
                        <button class="btn ghost icon" aria-label={t.up(item.q)} disabled={i === 0} onClick={() => moveFaq(i, -1)}>
                          ↑
                        </button>
                        <button
                          class="btn ghost icon"
                          aria-label={t.down(item.q)}
                          disabled={i === config.faq.length - 1}
                          onClick={() => moveFaq(i, 1)}
                        >
                          ↓
                        </button>
                        <button class="btn ghost icon" aria-label={t.remove(item.q)} onClick={() => set("faq", config.faq.filter((x) => x !== item))}>
                          ×
                        </button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p class="hint">{t.faqNone}</p>
            )}
            {isAdmin && config.faq.length < MAX_FAQ ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  addFaq();
                }}
              >
                <label>
                  {t.faqQuestion}
                  <input type="text" maxLength={120} value={newFaq.q} onInput={(e) => setNewFaq({ ...newFaq, q: e.currentTarget.value })} />
                </label>
                <label>
                  {t.faqAnswer}
                  <textarea class="plain" rows={3} maxLength={1000} value={newFaq.a} onInput={(e) => setNewFaq({ ...newFaq, a: e.currentTarget.value })} />
                </label>
                <button class="btn" style={{ marginTop: "8px" }} disabled={!newFaq.q.trim() || !newFaq.a.trim()}>
                  {t.faqAdd}
                </button>
              </form>
            ) : null}
          </Section>

          <Section title={t.presetTitle} intro={t.presetIntro}>
            {canTheme ? null : <FeatureHint feature="themes" />}
            <label class="check-line">
              <input type="checkbox" checked={applyColours} disabled={disabled} onChange={(e) => setApplyColours(e.currentTarget.checked)} />
              {t.applyColours}
            </label>
            <div class="preset-grid" role="radiogroup" aria-label={t.presetTitle}>
              {PRESET_IDS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={config.preset === id}
                  class={`preset-card${config.preset === id ? " on" : ""}`}
                  disabled={disabled || (!canTheme && id !== "classic" && id !== config.preset)}
                  onClick={() => setConfig(selectPreset(config, id, applyColours))}
                >
                  <WidgetMock config={cardConfig(config, id)} scale={0.278} />
                  <span class="preset-name">
                    <span class="preset-swatch" style={{ background: PRESETS[id].primary }} />
                    {t.presets[id].name}
                  </span>
                  <span class="preset-desc">{t.presets[id].desc}</span>
                </button>
              ))}
            </div>
            {/* Colours are not a themes field (see the server's needsThemes:
                preset, radius, font, launcherStyle, launcherText only), so the
                suggested palette stays available on every plan. */}
            {isAdmin && !usesSuggestedColours(config) ? (
              <button type="button" class="btn ghost preset-apply" onClick={() => setConfig(selectPreset(config, config.preset, true))}>
                <span class="preset-swatch" style={{ background: PRESETS[config.preset].primary }} />
                {t.applySuggested(t.presets[config.preset].name)}
              </button>
            ) : null}
          </Section>

          <Section title={t.look}>
            <div class="grid-2">
              <label>
                {t.primary}
                <span class="color-field">
                  <input type="color" value={config.primaryColor} disabled={disabled} onInput={(e) => set("primaryColor", e.currentTarget.value)} />
                  <code>{config.primaryColor}</code>
                </span>
              </label>
              <label>
                {t.onPrimary}
                <span class="color-field">
                  <input type="color" value={config.onPrimaryColor} disabled={disabled} onInput={(e) => set("onPrimaryColor", e.currentTarget.value)} />
                  <code>{config.onPrimaryColor}</code>
                </span>
              </label>
              <label>
                {t.position}
                <select value={config.position} disabled={disabled} onChange={(e) => set("position", e.currentTarget.value as WidgetConfig["position"])}>
                  <option value="bottom-right">{t.right}</option>
                  <option value="bottom-left">{t.left}</option>
                </select>
              </label>
              <label>
                {t.theme}
                <select value={config.theme} disabled={disabled} onChange={(e) => set("theme", e.currentTarget.value as WidgetConfig["theme"])}>
                  <option value="auto">{t.themeAuto}</option>
                  <option value="light">{t.themeLight}</option>
                  <option value="dark">{t.themeDark}</option>
                </select>
              </label>
              <label>
                {t.offsetX}
                <input type="number" min={0} max={200} value={config.offsetX} disabled={disabled} onInput={(e) => set("offsetX", clamp(e.currentTarget.value))} />
              </label>
              <label>
                {t.offsetY}
                <input type="number" min={0} max={200} value={config.offsetY} disabled={disabled} onInput={(e) => set("offsetY", clamp(e.currentTarget.value))} />
              </label>
            </div>
          </Section>

          <Section title={t.fineTune} intro={t.fineTuneIntro}>
            {canTheme ? null : <FeatureHint feature="themes" />}
            <div class="grid-2">
              <div class="range-field">
                <span>
                  {t.radius} <output class="hint">{config.radius === null ? t.radiusDefault : `${config.radius}px`}</output>
                </span>
                <input
                  type="range"
                  min={0}
                  max={28}
                  step={1}
                  aria-label={t.radius}
                  value={config.radius ?? 12}
                  disabled={disabled || !canTheme || config.radius === null}
                  onInput={(e) => setConfig(setRadius(config, Number(e.currentTarget.value)))}
                />
                {/* Ticking this resets the radius to the preset's own, which
                    is a move towards the default and so always allowed — a
                    downgraded workspace has to be able to undo fine-tuning. */}
                <label class="check-line">
                  <input
                    type="checkbox"
                    checked={config.radius === null}
                    disabled={disabled || (!canTheme && config.radius === null)}
                    onChange={(e) => setConfig(setRadius(config, e.currentTarget.checked ? null : 12))}
                  />
                  {t.radiusDefault}
                </label>
              </div>
              <label>
                {t.font}
                <select
                  value={config.font}
                  disabled={disabled || fonts.length < 2}
                  onChange={(e) => set("font", e.currentTarget.value as WidgetConfig["font"])}
                >
                  {fonts.map((id) => (
                    <option key={id} value={id}>
                      {FONT_LABEL[id](t)}
                    </option>
                  ))}
                </select>
                <span class="hint">{t.fontHint}</span>
              </label>
              <label>
                {t.launcher}
                <select
                  value={config.launcherStyle}
                  disabled={disabled || launchers.length < 2}
                  onChange={(e) => set("launcherStyle", e.currentTarget.value as WidgetConfig["launcherStyle"])}
                >
                  {launchers.map((id) => (
                    <option key={id} value={id}>
                      {LAUNCHER_LABEL[id](t)}
                    </option>
                  ))}
                </select>
              </label>
              {showsLauncherText(config) ? (
                <label>
                  {t.launcherText}
                  <input
                    type="text"
                    maxLength={24}
                    value={config.launcherText}
                    // Clearing it is a move back to the default, which the
                    // server allows; typing something new answers 402 and
                    // raises the notice at the top of the tab.
                    placeholder={config.locale === "en" ? "Chat" : "Sohbet"}
                    disabled={disabled || (!canTheme && config.launcherText === "")}
                    onInput={(e) => set("launcherText", e.currentTarget.value)}
                  />
                  <span class="hint">{t.launcherTextHint}</span>
                </label>
              ) : null}
            </div>
          </Section>

          <Section title={t.features}>
            <label>
              {t.contactAsk}
              <select
                value={config.contactAsk}
                disabled={disabled}
                onChange={(e) => set("contactAsk", e.currentTarget.value as WidgetConfig["contactAsk"])}
              >
                <option value="off">{t.contactAskOff}</option>
                <option value="optional">{t.contactAskOptional}</option>
                <option value="required">{t.contactAskRequired}</option>
              </select>
              <span class="hint">
                {t.contactAskHint[config.contactAsk]} {t.contactAskNote}
              </span>
            </label>
            <label>
              {t.endedShown}
              <select
                value={config.endedChatShownFor}
                disabled={disabled}
                onChange={(e) => set("endedChatShownFor", e.currentTarget.value as WidgetConfig["endedChatShownFor"])}
              >
                {(["none", "1h", "24h", "7d", "always"] as const).map((value) => (
                  <option key={value} value={value}>
                    {t.endedShownOptions[value]}
                  </option>
                ))}
              </select>
              <span class="hint">{t.endedShownHint}</span>
            </label>
            <Switch label={t.attachments} hint={t.attachmentsHint} checked={config.attachmentsEnabled} disabled={disabled} onChange={(v) => set("attachmentsEnabled", v)} />
            <Switch label={t.rating} hint={t.ratingHint} checked={config.ratingEnabled} disabled={disabled} onChange={(v) => set("ratingEnabled", v)} />
            <Switch label={t.typing} hint={t.typingHint} checked={config.showTypingIndicator} disabled={disabled} onChange={(v) => set("showTypingIndicator", v)} />
            <Switch label={t.sound} hint={t.soundHint} checked={config.soundEnabled} disabled={disabled} onChange={(v) => set("soundEnabled", v)} />
          </Section>
        </div>

        <aside class="widget-side">
          <WidgetMock config={config} scale={0.69} full label={t.preview} />
          {isAdmin ? (
            <div class="save-bar">
              <button class="btn primary" disabled={!dirty} onClick={() => void save()}>
                {all.common.saveChanges}
              </button>
              {dirty ? (
                <button class="btn" onClick={() => setConfig(saved)}>
                  {all.common.cancel}
                </button>
              ) : null}
            </div>
          ) : null}
          <StatusLine status={status} />
        </aside>
      </div>
    </>
  );
}
